import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { applyOps, summarizeElement } from "@overlay/runtime-patcher";
import type { EditOp } from "@overlay/op-schema";

type Selection = ReturnType<typeof summarizeElement>;

type MonacoEditor = {
  getValue: () => string;
  setValue: (v: string) => void;
  dispose: () => void;
  onDidChangeCursorPosition: (cb: (e: { position: { lineNumber: number; column: number } }) => void) => { dispose: () => void };
  getModel: () => { getOffsetAt: (p: { lineNumber: number; column: number }) => number } | null;
};

type MonacoGlobal = {
  editor: {
    create: (el: HTMLElement, options: Record<string, unknown>) => MonacoEditor;
    setTheme: (name: string) => void;
  };
};

const MONACO_CDN = "https://cdn.jsdelivr.net/npm/monaco-editor@0.52.2/min";

let monacoPromise: Promise<MonacoGlobal> | null = null;

function loadMonaco(): Promise<MonacoGlobal> {
  if (monacoPromise) return monacoPromise;
  monacoPromise = new Promise<MonacoGlobal>((resolve, reject) => {
    const w = window as unknown as {
      monaco?: MonacoGlobal;
      require?: { config: (cfg: unknown) => void } & ((deps: string[], cb: () => void) => void);
    };
    if (w.monaco) return resolve(w.monaco);
    const existing = document.querySelector<HTMLScriptElement>(`script[data-monaco-loader]`);
    const afterLoaderReady = () => {
      const req = (window as unknown as { require: { config: (cfg: unknown) => void } & ((deps: string[], cb: () => void) => void) }).require;
      req.config({ paths: { vs: `${MONACO_CDN}/vs` } });
      req(["vs/editor/editor.main"], () => {
        const m = (window as unknown as { monaco: MonacoGlobal }).monaco;
        resolve(m);
      });
    };
    if (existing) {
      if ((window as unknown as { require?: unknown }).require) afterLoaderReady();
      else existing.addEventListener("load", afterLoaderReady, { once: true });
      return;
    }
    const s = document.createElement("script");
    s.src = `${MONACO_CDN}/vs/loader.js`;
    s.dataset.monacoLoader = "1";
    s.onload = afterLoaderReady;
    s.onerror = () => reject(new Error("Failed to load Monaco"));
    document.head.appendChild(s);
  });
  return monacoPromise;
}

export function CodeTab({
  selection,
  beginPick,
  clearSelection,
  onApply,
}: {
  selection: Selection;
  beginPick: () => void;
  clearSelection: () => void;
  onApply: (ops: EditOp[]) => Promise<void>;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<MonacoEditor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);
  const [applying, setApplying] = useState(false);
  const revertRef = useRef<(() => void) | null>(null);
  const [previewApplied, setPreviewApplied] = useState(false);

  const initialHtml = useMemo(() => {
    try {
      const el = document.querySelector(selection.selector) as HTMLElement | null;
      return el ? formatHtml(el.outerHTML) : "";
    } catch {
      return "";
    }
  }, [selection.selector]);

  useEffect(() => {
    let cancelled = false;
    let cursorSub: { dispose: () => void } | null = null;
    let changeSub: { dispose: () => void } | null = null;
    (async () => {
      try {
        const monaco = await loadMonaco();
        if (cancelled || !containerRef.current) return;
        monaco.editor.setTheme("vs-dark");
        const editor = monaco.editor.create(containerRef.current, {
          value: initialHtml,
          language: "html",
          theme: "vs-dark",
          automaticLayout: true,
          minimap: { enabled: false },
          fontSize: 12,
          wordWrap: "on",
          scrollBeyondLastLine: false,
          tabSize: 2,
          lineNumbersMinChars: 3,
        });
        editorRef.current = editor;
        setLoading(false);

        // Editor change → mark dirty
        const model = editor.getModel() as unknown as {
          onDidChangeContent: (cb: () => void) => { dispose: () => void };
        } | null;
        if (model) {
          changeSub = model.onDidChangeContent(() => {
            setDirty(true);
          });
        }

        // Cursor change → try to highlight the element containing that offset in the real DOM
        cursorSub = editor.onDidChangeCursorPosition((e) => {
          const m = editor.getModel();
          if (!m) return;
          const offset = m.getOffsetAt(e.position);
          highlightElementAtOffset(editor.getValue(), offset, selection.selector);
        });
      } catch (err) {
        setError((err as Error).message || "Monaco failed to load");
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      cursorSub?.dispose();
      changeSub?.dispose();
      editorRef.current?.dispose();
      editorRef.current = null;
      revertRef.current?.();
      revertRef.current = null;
      clearHighlight();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection.selector]);

  // When the editor already exists but selection changed, reset value
  useEffect(() => {
    if (!editorRef.current) return;
    editorRef.current.setValue(initialHtml);
    setDirty(false);
    revertRef.current?.();
    revertRef.current = null;
    setPreviewApplied(false);
  }, [initialHtml]);

  const preview = () => {
    if (!editorRef.current) return;
    revertRef.current?.();
    revertRef.current = null;
    const html = editorRef.current.getValue();
    const op: EditOp = {
      id: `code-preview-${Date.now()}`,
      type: "html.replace",
      target: { selector: selection.selector },
      html,
    };
    const result = applyOps([op]);
    if (result.failed.length > 0) {
      setError("Preview failed: selector no longer matches.");
      return;
    }
    revertRef.current = result.revert;
    setPreviewApplied(true);
    setError(null);
  };

  const revertPreview = () => {
    revertRef.current?.();
    revertRef.current = null;
    setPreviewApplied(false);
  };

  const apply = async () => {
    if (!editorRef.current) return;
    setApplying(true);
    try {
      const html = editorRef.current.getValue();
      // Apply immediately so the user sees the change, then commit.
      if (!previewApplied) {
        const result = applyOps([
          {
            id: `code-apply-${Date.now()}`,
            type: "html.replace",
            target: { selector: selection.selector },
            html,
          },
        ]);
        if (result.failed.length > 0) {
          setError("Apply failed: selector no longer matches.");
          setApplying(false);
          return;
        }
        revertRef.current = result.revert;
      }
      revertRef.current = null;
      await onApply([
        {
          id: `code-${Date.now()}`,
          type: "html.replace",
          target: { selector: selection.selector },
          html,
        },
      ]);
      setDirty(false);
      setPreviewApplied(false);
      // The DOM changed — the old selection no longer applies. Tell parent to clear.
      clearSelection();
    } finally {
      setApplying(false);
    }
  };

  return (
    <div class="code-pane">
      <div class="code-context">
        <div class="selection-card">
          <div class="row" style={{ justifyContent: "space-between" }}>
            <span class="muted">Editing · &lt;{selection.tag}&gt;</span>
            <div class="row" style={{ gap: "4px" }}>
              <button class="btn ghost" onClick={beginPick}>Pick another</button>
              <button class="btn ghost" onClick={clearSelection} aria-label="Clear">✕</button>
            </div>
          </div>
          <div class="sel" style={{ marginTop: "6px" }}>{selection.selector}</div>
        </div>
      </div>

      <div class="code-editor-wrap">
        {loading && <div class="muted" style={{ padding: "12px" }}>Loading Monaco…</div>}
        {error && <div class="error" style={{ padding: "8px 12px" }}>{error}</div>}
        <div ref={containerRef} class="code-editor" />
      </div>

      <div class="code-commit">
        <span class="muted" style={{ fontSize: "11px" }}>
          {previewApplied ? "Preview applied" : dirty ? "Unsaved edits" : "No changes"}
        </span>
        <div class="spacer" />
        {previewApplied ? (
          <button class="btn" onClick={revertPreview}>Revert preview</button>
        ) : (
          <button class="btn" onClick={preview} disabled={!dirty || loading || !!error}>
            Preview
          </button>
        )}
        <button
          class="btn primary"
          onClick={apply}
          disabled={!dirty || applying || loading || !!error}
        >
          {applying ? "Applying…" : "Apply"}
        </button>
      </div>
    </div>
  );
}

/* ---------- helpers ---------- */

// Format HTML with basic indentation. Good enough for element outerHTML.
function formatHtml(input: string): string {
  const VOID = new Set([
    "area", "base", "br", "col", "embed", "hr", "img", "input",
    "link", "meta", "source", "track", "wbr",
  ]);
  const tokens = tokenize(input);
  let depth = 0;
  const lines: string[] = [];
  const indent = () => "  ".repeat(depth);
  for (const t of tokens) {
    if (t.kind === "text") {
      const text = t.value.replace(/\s+/g, " ").trim();
      if (text) lines.push(indent() + text);
    } else if (t.kind === "open") {
      lines.push(indent() + t.value);
      if (!VOID.has(t.tag) && !t.value.endsWith("/>")) depth++;
    } else if (t.kind === "close") {
      depth = Math.max(0, depth - 1);
      lines.push(indent() + t.value);
    } else {
      lines.push(indent() + t.value);
    }
  }
  return lines.join("\n");
}

type Token =
  | { kind: "open"; value: string; tag: string; start: number; end: number }
  | { kind: "close"; value: string; tag: string; start: number; end: number }
  | { kind: "text"; value: string; start: number; end: number }
  | { kind: "other"; value: string; start: number; end: number };

function tokenize(src: string): Token[] {
  const out: Token[] = [];
  let i = 0;
  while (i < src.length) {
    if (src[i] === "<") {
      const end = src.indexOf(">", i);
      if (end === -1) {
        out.push({ kind: "text", value: src.slice(i), start: i, end: src.length });
        break;
      }
      const value = src.slice(i, end + 1);
      if (value.startsWith("</")) {
        const tagMatch = /^<\/\s*([a-zA-Z][\w-]*)/.exec(value);
        out.push({ kind: "close", value, tag: (tagMatch?.[1] || "").toLowerCase(), start: i, end: end + 1 });
      } else if (value.startsWith("<!") || value.startsWith("<?")) {
        out.push({ kind: "other", value, start: i, end: end + 1 });
      } else {
        const tagMatch = /^<\s*([a-zA-Z][\w-]*)/.exec(value);
        out.push({ kind: "open", value, tag: (tagMatch?.[1] || "").toLowerCase(), start: i, end: end + 1 });
      }
      i = end + 1;
    } else {
      const next = src.indexOf("<", i);
      const end = next === -1 ? src.length : next;
      out.push({ kind: "text", value: src.slice(i, end), start: i, end });
      i = end;
    }
  }
  return out;
}

/* ---------- Cursor → DOM highlight ---------- */

let highlightEl: HTMLDivElement | null = null;

function clearHighlight() {
  if (highlightEl) {
    highlightEl.remove();
    highlightEl = null;
  }
}

function showHighlight(rect: DOMRect) {
  if (!highlightEl) {
    highlightEl = document.createElement("div");
    Object.assign(highlightEl.style, {
      position: "fixed",
      border: "2px solid #7a66ff",
      borderRadius: "4px",
      pointerEvents: "none",
      zIndex: "2147483646",
      boxShadow: "0 0 0 9999px rgba(6,7,12,0.08)",
      transition: "all 80ms ease-out",
    } as Partial<CSSStyleDeclaration>);
    document.documentElement.appendChild(highlightEl);
  }
  highlightEl.style.left = `${rect.left - 3}px`;
  highlightEl.style.top = `${rect.top - 3}px`;
  highlightEl.style.width = `${rect.width + 6}px`;
  highlightEl.style.height = `${rect.height + 6}px`;
}

/**
 * Walk the formatted HTML up to `offset`, counting opens/closes to compute
 * the nested child path from the root element, then resolve to a real DOM node.
 */
function highlightElementAtOffset(source: string, offset: number, rootSelector: string) {
  const root = document.querySelector(rootSelector) as HTMLElement | null;
  if (!root) return;
  const tokens = tokenize(source);
  const path: number[] = []; // child index at each depth, relative to root
  const stack: number[] = []; // index within current parent's children, per depth
  let targetDepth = 0;
  let currentEl: Element = root;
  // We walk tokens until we pass `offset`; the innermost open tag we're inside defines the highlight.
  let innermost: Element | null = root;

  for (const t of tokens) {
    if (t.start > offset) break;
    if (t.kind === "open") {
      // First open is the root itself; subsequent opens descend into children.
      if (targetDepth === 0 && path.length === 0) {
        // root element — no descent
        targetDepth = 1;
        stack.push(0); // track child count within root
        continue;
      }
      // Within some parent: compute which child this open represents.
      const childIdx = stack[stack.length - 1] ?? 0;
      const parent: Element = currentEl;
      const children = parent.children;
      const child = children[childIdx];
      if (child) {
        currentEl = child;
        innermost = child;
      }
      // Going deeper:
      stack[stack.length - 1] = childIdx + 1;
      stack.push(0);
      targetDepth++;
      if (isSelfClosing(t.value, t.tag)) {
        // Pop back out
        stack.pop();
        targetDepth--;
        if (targetDepth > 0) currentEl = currentEl.parentElement ?? root;
      }
    } else if (t.kind === "close") {
      stack.pop();
      targetDepth = Math.max(0, targetDepth - 1);
      if (currentEl !== root) {
        currentEl = currentEl.parentElement ?? root;
      }
    }
  }

  if (innermost instanceof HTMLElement) {
    showHighlight(innermost.getBoundingClientRect());
  }
}

function isSelfClosing(tag: string, name: string): boolean {
  if (tag.endsWith("/>")) return true;
  return new Set([
    "area", "base", "br", "col", "embed", "hr", "img", "input",
    "link", "meta", "source", "track", "wbr",
  ]).has(name);
}

